import {
  EMBEDDED_PARAM,
  EMBEDDED_VALUE,
  isEmbeddedEditor,
  isMinimalEditorChrome,
  MINIMAL_CHROME_PARAM,
  MINIMAL_CHROME_VALUE
} from '../editorShell/editorChrome';
import type { DocumentEditorKind } from './documentFileTypes';

export type DocumentEditorMode = 'edit' | 'preview';

/** Reads the app's resolved light/dark theme so the editor iframe matches. */
export function resolveDxTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

/** Same-origin Razor host page that renders the DevExpress control inside the iframe. */
export function documentHostSrc(
  kind: DocumentEditorKind,
  mode: DocumentEditorMode,
  mountId: string,
  relativePath: string,
  theme: 'light' | 'dark' = resolveDxTheme(),
  discussionsEnabled = true
): string {
  const route =
    kind === 'spreadsheet'
      ? mode === 'edit'
        ? '/documents/spreadsheet-host'
        : '/documents/spreadsheet-preview-host'
      : mode === 'edit'
        ? '/documents/richedit-host'
        : '/documents/richedit-preview-host';

  const params = new URLSearchParams();
  params.set('mountId', mountId);
  params.set('path', relativePath);
  params.set('theme', theme);
  if (!discussionsEnabled) params.set('discussions', '0');
  return `${route}?${params.toString()}`;
}

/** SPA route that wraps the iframe (opened in a new tab from the file browser). */
export function documentEditorRoute(
  kind: DocumentEditorKind,
  mode: DocumentEditorMode
): string {
  if (mode === 'preview') return '/files/document-preview';
  return kind === 'spreadsheet' ? '/files/spreadsheet-editor' : '/files/document-editor';
}

export function buildDocumentEditorSearch(
  mountId: string,
  relativePath: string,
  chrome?: URLSearchParams | { get(name: string): string | null }
): string {
  const params = new URLSearchParams();
  params.set('mountId', mountId);
  params.set('path', relativePath);
  if (chrome && isEmbeddedEditor(chrome)) params.set(EMBEDDED_PARAM, EMBEDDED_VALUE);
  if (chrome && isMinimalEditorChrome(chrome)) params.set(MINIMAL_CHROME_PARAM, MINIMAL_CHROME_VALUE);
  return params.toString();
}

export function documentEditorHref(
  kind: DocumentEditorKind,
  mode: DocumentEditorMode,
  mountId: string,
  relativePath: string
): string {
  return `${documentEditorRoute(kind, mode)}?${buildDocumentEditorSearch(mountId, relativePath)}`;
}
